import type { InfiniteData } from '@tanstack/react-query';
import type { Translate } from 'next-translate';

import type { PrefetchConfig } from '~/common/hooks/usePrefetch';
import { arrayToMap } from '~/common/utils/arrayToMap';

import {
  getPredictionMarkets,
  getPredictionMarketsDiscover,
  getMarketGroupById,
} from '../services/market.service.api';
import {
  MarketQueryKeys,
  STALE_TIME,
  STALE_TIME_5_MIN,
  INFINITE_SCROLL_PAGE_SIZE,
  PREFETCH_PREVIEW_PAGE_SIZE,
} from '../constants/query';
import type {
  MarketGroupType,
  MarketSubCategory,
  MarketGroupDetailResponse,
  PredictionMarketsResponse,
} from '../services/interfaces/market.types';
import {
  getPositionById,
  getPositions,
} from '../domains/my-positions/services/positions.service.api';
import { PositionsQueryKeys } from '../domains/my-positions/constants/query';
import { syncPositionsData } from '../domains/my-positions/state/positions.sync';
import type {
  PositionDetailResponse,
  PositionsResponse,
} from '../domains/my-positions/services/interfaces/positions.types';
import { syncMarketDetailData } from '../state/marketDetail.sync';
import { useEntitiesStore } from '../state/entities.store';
import { useNormalizedMarketsStore } from '../state/normalizedMarkets.store';
import { selectSubCategoryFilters } from '../state/selectors/discover.selectors';

import { toMarketTypesSegment } from './marketGroupType';

function syncNewBrowsePages(pages: PredictionMarketsResponse[]): void {
  const existingIds = useNormalizedMarketsStore.getState().normalizedMarketGroupsById;
  const newPages = pages.filter((page) =>
    page.data.some((marketGroup) => !existingIds.has(marketGroup.id))
  );

  if (newPages.length === 0) return;

  const events = newPages.flatMap((page) => page.included.events);
  const teams = newPages.flatMap((page) => page.included.teams);
  const entities = newPages.flatMap((page) => page.included.entities);
  const subCategories = newPages.flatMap((page) => page.included.sub_categories ?? []);
  const marketGroups = newPages.flatMap((page) => page.data);

  useEntitiesStore.getState().mergeEntities(events, teams, entities, subCategories);
  useNormalizedMarketsStore.getState().appendBrowseData(marketGroups, arrayToMap(events));
}

function getSubCategoryTypeSets(sport: MarketSubCategory): (MarketGroupType[] | undefined)[] {
  const filters = selectSubCategoryFilters(
    useNormalizedMarketsStore.getState().subCategories,
    sport
  );

  if (!filters.length) return [undefined];

  const seen = new Set<string>();

  return filters.reduce<MarketGroupType[][]>((typeSets, filter) => {
    const segment = toMarketTypesSegment(filter.types);

    if (filter.types.length > 0 && !seen.has(segment)) {
      seen.add(segment);
      typeSets.push(filter.types);
    }

    return typeSets;
  }, []);
}

export function createMarketPrefetchConfig(
  sport: MarketSubCategory
): PrefetchConfig<PredictionMarketsResponse[]> {
  return {
    queryKey: MarketQueryKeys.PREDICTION_MARKETS_PREVIEW(sport),
    queryFn: () =>
      Promise.all(
        getSubCategoryTypeSets(sport).map((types) =>
          getPredictionMarkets({
            sub_category: sport,
            types,
            offset: 0,
            limit: PREFETCH_PREVIEW_PAGE_SIZE,
          })
        )
      ),
    staleTime: STALE_TIME_5_MIN,
    onSuccess: syncNewBrowsePages,
  };
}

export function createDiscoverPrefetchConfig(): PrefetchConfig {
  return {
    queryKey: MarketQueryKeys.PREDICTION_MARKETS_DISCOVER(),
    queryFn: () => getPredictionMarketsDiscover(),
    staleTime: STALE_TIME_5_MIN,
  };
}

export function createPositionsPrefetchConfig(t: Translate): PrefetchConfig {
  return {
    queryKey: PositionsQueryKeys.POSITIONS({ status: 'open' }),
    queryFn: () => getPositions({ status: 'open', offset: 0, limit: INFINITE_SCROLL_PAGE_SIZE }),
    staleTime: STALE_TIME,
    isInfinite: true,
    onSuccess: (data) => {
      const infiniteData = data as InfiniteData<PositionsResponse>;
      infiniteData.pages.forEach((page, index) => {
        syncPositionsData(page, t, index === 0);
      });
    },
  };
}

export function createPositionDetailPrefetchConfig(
  positionId: string
): PrefetchConfig<PositionDetailResponse> {
  return {
    queryKey: PositionsQueryKeys.POSITION_DETAIL(positionId),
    queryFn: () => getPositionById(positionId),
    staleTime: STALE_TIME,
  };
}

export function createMarketDetailPrefetchConfig(
  marketGroupId: string
): PrefetchConfig<MarketGroupDetailResponse> {
  return {
    queryKey: MarketQueryKeys.PREDICTION_MARKET(marketGroupId),
    queryFn: ({ signal }) => getMarketGroupById(marketGroupId, { signal }),
    staleTime: STALE_TIME,
    onSuccess: (data) => {
      const existingMarketGroup = useNormalizedMarketsStore
        .getState()
        .normalizedMarketGroupsById.get(marketGroupId);

      if (!existingMarketGroup) {
        syncMarketDetailData(data);
      }
    },
  };
}
